import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import type { HistoryItem } from 'BrokerTsLib/src/dtos'
import { ActionHistoryTypes } from 'BrokerTsLib/src/dtos'

import { UserLink } from '@/components/UserLink'
import { useGetInsurance } from '@/hooks/useInsuranceParams'

import { useInsuranceParams } from '../../../context/InsuranceParamsProvider'

export interface HistoryEntryProps extends HistoryItem {
  isLast?: boolean
}

const HistoryEntryTimeline = ({ hasLine = true }: { hasLine?: boolean }) => {
  return (
    <Grid
      sx={{
        gap: {
          base: 1,
          lg: 2,
        },
        alignItems: 'start',
        justifyItems: 'center',
        gridTemplateRows: '12px auto',
        pt: 1,
      }}
    >
      <Box
        sx={{
          w: 3,
          h: 3,
          bgColor: 'wizzard-header-history-timeline-dot-bg',
          borderRadius: 'full',
        }}
      ></Box>

      {hasLine && (
        <Box
          sx={{
            w: '1px',
            h: '100%',
            bgColor: 'wizzard-header-history-timeline-line-bg',
          }}
        ></Box>
      )}
    </Grid>
  )
}

export const HistoryEntry = ({
  type,
  actionType,
  dateCreated,
  fullName,
  offerId,
  isLast = false,
  ...rest
}: HistoryEntryProps) => {
  const { insuranceParamsId } = useInsuranceParams()
  const { data } = useGetInsurance(offerId!, { enabled: !!offerId })

  let title = ''
  let desc = ''
  switch (type) {
    case ActionHistoryTypes.CreatedBusinessCase:
      title = 'Vytvoření nabídky'
      desc = 'Vytvořena nabídka č. ' + insuranceParamsId
      break
    case ActionHistoryTypes.SelectedOffer:
      title = 'Výběr kalkulace'
      desc = 'Vybrána kalkulace č. ' + offerId
      break
    case ActionHistoryTypes.EmailCalculation:
    case ActionHistoryTypes.EmailCalculationGdpr:
      title = 'Odeslán email'
      desc = 'Odeslaná nabídka pojištění'
      break
    case ActionHistoryTypes.CreateContract:
      title = 'Sjednáno pojištění'
      desc = 'Sjednána sml. č. ' + (data?.data.contractNumber ?? '')
      break
    case ActionHistoryTypes.EmailContract:
    case ActionHistoryTypes.EmailContractGdpr:
      title = 'Odeslán email'
      desc = 'Odeslaná pojistka'
      break
    case ActionHistoryTypes.CreateContractFailed:
      title = 'Chyba při sjednávání'
      desc = 'Sjednávání nabídky č. ' + offerId + ' selhalo'
      break
    case ActionHistoryTypes.CreateContractStarted:
      title = 'Sjednávání zahájeno'
      desc = 'Zahájeno sjednávání nabídky č. ' + offerId
      break
    default:
      title = actionType // TODO
      desc = ''
      break
  }

  return (
    <Grid
      sx={{
        gridTemplateColumns: '12px auto',
        gap: {
          base: 2,
          lg: 4,
        },
      }}
    >
      <HistoryEntryTimeline hasLine={!isLast} />

      <Flex
        sx={{
          gap: {
            base: 2,
            xl: 4,
          },
          pb: {
            base: 1,
            lg: 2,
          },
          flexWrap: 'wrap',
        }}
      >
        <Grid sx={{ alignContent: 'start' }}>
          <Text
            sx={{
              minW: 32,
              color: 'wizzard-header-history-timeline-label-color',
              fontSize: {
                base: 'xs',
                xl: 'sm',
              },
              fontWeight: 'medium',
            }}
          >
            {title}
          </Text>
          <Text sx={{ color: 'gray.400', fontSize: 'xs' }}>
            {Intl.DateTimeFormat('cs-CZ', {
              dateStyle: 'medium',
              timeStyle: 'medium',
            }).format(new Date(dateCreated))}
          </Text>
        </Grid>

        <Grid
          sx={{
            display: {
              base: 'none',
              lg: 'grid',
            },
          }}
        >
          <UserLink userId={rest.userId} name={fullName} sx={{ fontWeight: 'medium', fontSize: { base: 'xs', xl: 'sm' }, color: 'wizzard-header-history-timeline-user-color' }} />
          <Text
            sx={{
              color: 'wizzard-header-history-timeline-description-color',
              fontSize: { base: 'xs', xl: 'sm' },
            }}
          >
            {desc}
          </Text>
        </Grid>
      </Flex>
    </Grid>
  )
}
